import { observer } from 'mobx-react-lite'
import { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { authStore } from '../../lib/stores/authStore'
import keycloak from '../../config/keycloak'
import { CircularProgress, Box, Typography, Button } from '@mui/material'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import { usePermission } from '../../lib/hooks/usePermission'

interface ProtectedRouteProps {
  children: React.ReactNode
}

/**
 * Paths that are accessible to any authenticated user regardless of permissions.
 * These are not menu items in the role_menu_action list.
 */
const OPEN_PATHS = ['/profile', '/notifications', '/forms']

const ProtectedRoute = observer(({ children }: ProtectedRouteProps) => {
  const [isInitialising, setIsInitialising] = useState(false)
  const [initDone, setInitDone] = useState(false)
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { canViewMenu } = usePermission()

  useEffect(() => {
    if (!keycloak.authenticated) return
    if (authStore.isAuthenticated || initDone) return

    // Keycloak is authenticated but authStore has no user yet.
    // This happens on fresh login AND on every page refresh (user is not persisted in memory).
    // Read all context directly from the Keycloak token — no API round-trip needed.
    const init = async () => {
      try {
        setIsInitialising(true)
        await authStore.initFromKeycloakToken()
        setInitDone(true)
        // Load extended profile (orgName, lobName, czentrixAgentId) in the background
        authStore.loadProfile().catch(() => {/* non-fatal */})
      } catch (err) {
        setInitDone(true)
      } finally {
        setIsInitialising(false)
      }
    }

    init()
  }, [keycloak.authenticated, initDone])

  // Not authenticated in Keycloak — redirect to Keycloak login
  if (!keycloak.authenticated) {
    keycloak.login()
    return null
  }

  // Token initialisation in progress
  if (isInitialising || !authStore.isAuthenticated) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          backgroundColor: '#f8fafc',
        }}
      >
        <CircularProgress size={40} thickness={4} sx={{ color: '#1a56db' }} />
        <Typography variant="body2" sx={{ color: '#475569', fontWeight: 500 }}>
          Signing you in...
        </Typography>
      </Box>
    )
  }

  // ── Permission check based on current route ─────────────────────────────────
  // Skip for open paths that aren't part of the role_menu_action list.
  const isOpenPath = OPEN_PATHS.some(p => pathname === p || pathname.startsWith(p + '/'))
  const hasNoPermissions = Object.keys(authStore.user?.permissionsMap ?? {}).length === 0

  if (!isOpenPath && !canViewMenu(pathname)) {
    return (
      <Box
        sx={{
          display: 'flex',
          height: '100vh',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f8fafc',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            p: 4,
            bgcolor: '#fff',
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
            maxWidth: 420,
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              width: 56, height: 56, borderRadius: '50%',
              bgcolor: '#fef2f2', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
            }}
          >
            <LockOutlinedIcon sx={{ fontSize: 28, color: '#ef4444' }} />
          </Box>
          <Typography fontWeight={700} fontSize="18px" color="#1e293b">
            {hasNoPermissions ? 'Unauthorized' : 'Access Denied'}
          </Typography>
          <Typography fontSize="13.5px" color="#64748b" lineHeight={1.6}>
            {hasNoPermissions
              ? 'No permissions have been assigned to your account. You cannot access features in this portal. Please contact your system administrator.'
              : "You don't have permission to view this page. Contact your admin if you think this is a mistake."}
          </Typography>
          <Button
            variant="contained"
            size="small"
            onClick={() => navigate(-1)}
            sx={{
              mt: 1, bgcolor: '#1a56db', borderRadius: '8px',
              textTransform: 'none', fontWeight: 600,
              '&:hover': { bgcolor: '#1e40af' },
            }}
          >
            Go Back
          </Button>
        </Box>
      </Box>
    )
  }

  return <>{children}</>
})

export default ProtectedRoute

